import { AudioTrack, useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallVolumeStore } from "../store/callVolumeStore";
import { useAudioPlaybackUnlock } from "../hooks/useAudioPlaybackUnlock";

/**
 * Drop-in replacement for LiveKit's <RoomAudioRenderer> that applies the
 * session volume (#1722) to remote participants' MICROPHONE tracks only.
 *
 * Why not `<RoomAudioRenderer volume>`: that prop hits every remote audio
 * track, including screen-share audio, which must stay at full level.
 *
 * Why this is audible: with `webAudioMix` LiveKit mutes the <audio> element
 * and plays through a GainNode, so writing `el.volume` does nothing. Passing
 * `volume` to <AudioTrack> makes it call `RemoteAudioTrack.setVolume()` in an
 * effect that runs after the track is attached, which drives the GainNode —
 * including for late joiners and at exactly 0 (LiveKit's own re-apply on
 * attach skips a falsy stored volume).
 *
 * It also turns audio back on when the browser has paused it (see
 * `useAudioPlaybackUnlock`): the next gesture anywhere resumes it, and a
 * prompt says so meanwhile.
 */
export function CallAudioRenderer() {
  const volume = useCallVolumeStore((s) => s.volume);
  const tracks = useTracks(
    [Track.Source.Microphone, Track.Source.ScreenShareAudio, Track.Source.Unknown],
    { updateOnlyOn: [], onlySubscribed: true },
  ).filter((ref) => !ref.participant.isLocal && ref.publication.kind === Track.Kind.Audio);

  const { blocked, unlock } = useAudioPlaybackUnlock();
  const { t } = useTranslation();

  return (
    <>
      <div style={{ display: "none" }}>
        {tracks.map((trackRef) => (
          <AudioTrack
            key={`${trackRef.participant.identity}_${trackRef.publication.trackSid}`}
            trackRef={trackRef}
            volume={trackRef.source === Track.Source.Microphone ? volume / 100 : undefined}
          />
        ))}
      </div>
      {blocked && (
        <div
          role="status"
          className="fixed top-4 inset-x-0 z-[1000] flex justify-center pointer-events-none px-4"
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-foreground text-background shadow-lg ps-4 pe-1.5 py-1.5 text-sm">
            <span>{t("call.connection.audioBlocked")}</span>
            <button
              type="button"
              onClick={unlock}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-semibold"
            >
              <Volume2 className="size-4" aria-hidden />
              {t("call.connection.audioBlockedAction")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
